import React, { useState, useEffect } from 'react';
import { 
  DollarSign, CreditCard, Banknote, Smartphone, Building2,
  TrendingUp, TrendingDown, Clock, AlertCircle, CheckCircle,
  ChevronRight, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/helpers';
import Loading from '../Loading';

const PaymentSummary = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month'); // week, month, quarter, year
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/reports/payment-summary?period=${period}`);
      setData(response.data);
    } catch (err) {
      console.error('Error loading payment summary:', err);
      setError('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      'Cash': Banknote,
      'Check': Building2,
      'Credit Card': CreditCard,
      'Debit Card': CreditCard,
      'Venmo': Smartphone,
      'Zelle': Smartphone,
      'PayPal': Smartphone,
      'Bank Transfer': Building2,
      'Stripe': CreditCard,
    };
    return icons[method] || DollarSign;
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      'Cash': 'bg-green-100 text-green-700',
      'Check': 'bg-blue-100 text-blue-700',
      'Credit Card': 'bg-purple-100 text-purple-700',
      'Debit Card': 'bg-purple-100 text-purple-700',
      'Venmo': 'bg-blue-100 text-blue-700',
      'Zelle': 'bg-violet-100 text-violet-700',
      'PayPal': 'bg-indigo-100 text-indigo-700',
      'Bank Transfer': 'bg-slate-100 text-slate-700',
      'Stripe': 'bg-violet-100 text-violet-700',
    };
    return colors[method] || 'bg-gray-100 text-gray-700';
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'quarter': return 'This Quarter';
      case 'year': return 'This Year';
      default: return 'This Month';
    }
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-800">{error}</p>
        <button 
          onClick={loadData}
          className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const {
    totalCollected = 0,
    previousPeriodCollected = 0,
    paymentsByMethod = [],
    outstandingAmount = 0,
    outstandingCount = 0,
    oldestOutstandingDays = 0,
    collectionRate = 0,
    recentPayments = [],
    averagePaymentTime = null,
  } = data || {};

  const hasBaseline = previousPeriodCollected >= 500;
  const changePercent = hasBaseline
    ? ((totalCollected - previousPeriodCollected) / previousPeriodCollected * 100).toFixed(0)
    : null;
  const isUp = changePercent === null || changePercent >= 0;

  // Calculate max for bar chart
  const maxPayment = Math.max(...paymentsByMethod.map(p => p.amount), 1);

  return (
    <div className="space-y-4">
      {/* Period Selector */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {['week', 'month', 'quarter', 'year'].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              period === p
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p === 'week' ? 'Week' : p === 'month' ? 'Month' : p === 'quarter' ? 'Quarter' : 'Year'}
          </button>
        ))}
      </div>

      {/* Main Stats Cards - 2x2 grid on mobile, 4 cols on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Total Collected */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between mb-1">
            <span className="text-green-100 text-xs font-medium">Collected</span>
            <DollarSign size={16} className="text-green-200" />
          </div>
          <p className="text-2xl font-bold">{formatCurrency(totalCollected)}</p>
          <div className="flex items-center gap-1 mt-1">
            {isUp ? (
              <ArrowUpRight size={14} className="text-green-200" />
            ) : (
              <ArrowDownRight size={14} className="text-red-200" />
            )}
            <span className={`text-xs ${isUp ? 'text-green-200' : 'text-red-200'}`}>
              {changePercent === null ? 'No comparison yet' : `${isUp ? '+' : ''}${changePercent}% vs last ${period}`}
            </span>
          </div>
        </div>

        {/* Outstanding */}
        <div className={`rounded-xl p-4 ${outstandingAmount > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="flex items-center justify-between mb-1">
            <span className={`text-xs font-medium ${outstandingAmount > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
              Outstanding
            </span>
            <Clock size={16} className={outstandingAmount > 0 ? 'text-amber-500' : 'text-gray-400'} />
          </div>
          <p className={`text-2xl font-bold ${outstandingAmount > 0 ? 'text-amber-900' : 'text-gray-900'}`}>
            {formatCurrency(outstandingAmount)}
          </p>
          <p className={`text-xs mt-1 ${outstandingAmount > 0 ? 'text-amber-600' : 'text-gray-500'}`}>
            {outstandingCount > 0 
              ? `${outstandingCount} invoice${outstandingCount > 1 ? 's' : ''} unpaid`
              : 'All caught up! 🎉'
            }
          </p>
        </div>

        {/* Collection Rate */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-blue-700 text-xs font-medium">Collection Rate</span>
            <CheckCircle size={16} className="text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-900">{collectionRate}%</p>
          <p className="text-xs text-blue-600 mt-1">
            of invoiced amount
          </p>
        </div>

        {/* Avg Payment Time */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-purple-700 text-xs font-medium">Avg Pay Time</span>
            <Calendar size={16} className="text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-purple-900">
            {averagePaymentTime == null ? '—' : averagePaymentTime === 0 ? 'Same day' : `${averagePaymentTime}d`}
          </p>
          <p className="text-xs text-purple-600 mt-1">
            days to get paid
          </p>
        </div>
      </div>

      {/* Payments by Method */}
      {paymentsByMethod.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            By Payment Method
          </h3>
          <div className="space-y-3">
            {paymentsByMethod.map((item, index) => {
              const Icon = getPaymentMethodIcon(item.method);
              const barWidth = (item.amount / maxPayment * 100).toFixed(0);
              const percent = totalCollected > 0 
                ? (item.amount / totalCollected * 100).toFixed(0) 
                : 0;
              
              return (
                <div key={item.method || index}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${getPaymentMethodColor(item.method)}`}>
                        <Icon size={14} />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{item.method}</span>
                      <span className="text-xs text-gray-500">({item.count})</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(item.amount)}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">
                        ({percent}%)
                      </span>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Outstanding Invoices Warning */}
      {outstandingCount > 0 && oldestOutstandingDays > 7 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-amber-900">Follow up on payments</h4>
              <p className="text-sm text-amber-700 mt-0.5">
                You have {formatCurrency(outstandingAmount)} outstanding across {outstandingCount} invoice{outstandingCount > 1 ? 's' : ''}.
                {oldestOutstandingDays > 0 && (
                  <span> Oldest is {oldestOutstandingDays} days old.</span>
                )}
              </p>
              <a 
                href="/invoices?status=unpaid" 
                className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-900 mt-2"
              >
                View unpaid invoices
                <ChevronRight size={14} />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Recent Payments */}
      {recentPayments.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Recent Payments</h3>
            <a 
              href="/invoices?status=paid" 
              className="text-xs text-primary-600 hover:text-primary-700"
            >
              View all
            </a>
          </div>
          <div className="space-y-2">
            {recentPayments.slice(0, 5).map((payment, index) => {
              const Icon = getPaymentMethodIcon(payment.method);
              return (
                <div 
                  key={payment.id || index}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getPaymentMethodColor(payment.method)}`}>
                      <Icon size={14} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {payment.customerName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {payment.method} • {new Date(payment.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-green-600 flex-shrink-0">
                    +{formatCurrency(payment.amount)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {totalCollected === 0 && outstandingAmount === 0 && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
          <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No payments yet</h3>
          <p className="text-sm text-gray-600">
            Once you start recording payments, you'll see your summary here.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentSummary;